import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquareText, Send, ChevronDown, ShieldCheck, ImagePlus, Loader2, X } from "lucide-react";
import { shrinkImage } from "@/lib/image";

const img = (p) => `${API}/files/${p}?auth=${getToken()}`;

const QUICK = {
    customer: ["What time will you arrive?", "Please call me from the app when you reach", "Gate pass is ready", "Do I need to arrange anything?", "Thank you!"],
    professional: ["I'm on the way", "Reached — please open the gate", "Running 10 minutes late", "Please keep the area clear", "Work done, please review in the app"],
};

// In-app chat between the customer and the assigned professional (contact details auto-hidden).
export function BookingChat({ bookingId, role }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(null);
    const fileRef = useRef(null);
    const endRef = useRef(null);
    const load = useCallback((read = false) => api.get(`/bookings/${bookingId}/chat${read ? "?mark_read=true" : ""}`).then((r) => setData(r.data)).catch(() => {}), [bookingId]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const t = setInterval(() => load(open), open ? 6000 : 30000);
        return () => { clearInterval(t); };
    }, [open, load]);
    useEffect(() => { if (open) { endRef.current?.scrollIntoView({ block: "end" }); } }, [open, data?.messages?.length]);
    if (!data || (!data.open && !data.messages.length)) return null;
    const unread = data.messages.filter((m) => m.from_role !== role && !m.read).length;
    const other = role === "customer" ? (data.pro_name || "your professional") : (data.customer_name || "customer");
    const send = async (t) => {
        const msg = (t ?? text).trim();
        if (!msg) return;
        setBusy(true);
        try { const { data: m } = await api.post(`/bookings/${bookingId}/chat`, { text: msg }); setText(""); if (m.masked) toast("Contact details are hidden — please keep all calls & payments inside HOMIIE."); await load(true); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const sendPhoto = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Please choose a photo");
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", await shrinkImage(f));
            const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post(`/bookings/${bookingId}/chat`, { text: text.trim(), photo: up.path });
            setText(""); await load(true);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div data-testid="booking-chat" className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
            <button data-testid="booking-chat-toggle" onClick={() => { setOpen(!open); if (!open) load(true); }} className="flex w-full items-center gap-3 p-4 text-left">
                <span className="rounded-xl bg-teal/10 p-2 text-teal"><MessageSquareText size={18} /></span>
                <span className="flex-1"><span className="block font-heading font-bold text-navy">Chat with {other}</span><span className="text-xs text-navy/50">{data.open ? "Numbers stay private" : "Chat closed — read only"}</span></span>
                {unread > 0 && !open && <span data-testid="booking-chat-unread" className="rounded-full bg-orange px-2 py-0.5 text-[11px] font-bold text-white">{unread}</span>}
                <ChevronDown size={18} className={`text-navy/40 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="border-t border-navy/10">
                    <div className="max-h-72 space-y-2 overflow-y-auto bg-navy/[0.02] p-3" data-testid="booking-chat-messages">
                        {data.messages.length === 0 && <p className="py-6 text-center text-xs text-navy/40">Say hello — ask anything about this service.</p>}
                        {data.messages.map((m) => {
                            const mine = m.from_role === role;
                            return (
                                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-sm bg-teal text-white" : "rounded-bl-sm bg-white text-navy shadow-sm"}`}>
                                        {m.photo && <button type="button" data-testid={`chat-photo-${m.id}`} onClick={() => setZoom(m.photo)} className="mb-1 block overflow-hidden rounded-xl"><img src={img(m.photo)} alt="Shared" className="max-h-48 w-full object-cover" /></button>}
                                        {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                                        <p className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-navy/40"}`}>{new Date(m.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}{mine && m.read ? " · Seen" : ""}</p>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={endRef} />
                    </div>
                    {data.open && (
                        <div className="space-y-2 p-3">
                            <div className="flex gap-1.5 overflow-x-auto pb-1 homiie-scroll">
                                {QUICK[role].map((q) => <button key={q} data-testid={`chat-quick-${q.replace(/[^a-z]/gi, "").slice(0, 18).toLowerCase()}`} disabled={busy} onClick={() => send(q)} className="shrink-0 rounded-full border border-teal/30 bg-teal/5 px-3 py-1.5 text-xs font-semibold text-teal">{q}</button>)}
                            </div>
                            <div className="flex gap-2">
                                <button data-testid="booking-chat-photo-btn" disabled={busy} onClick={() => fileRef.current?.click()} title="Share a photo" className="rounded-full border border-teal/30 bg-teal/5 p-3 text-teal disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}</button>
                                <input ref={fileRef} data-testid="booking-chat-photo-input" type="file" accept="image/*" className="hidden" onChange={sendPhoto} />
                                <input data-testid="booking-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Type a message…" className="flex-1 rounded-full border border-navy/15 px-4 py-2.5 text-sm outline-none focus:border-teal" />
                                <button data-testid="booking-chat-send" disabled={busy || !text.trim()} onClick={() => send()} className="rounded-full bg-teal p-3 text-white disabled:opacity-50"><Send size={16} /></button>
                            </div>
                            <p className="flex items-center gap-1 text-[10px] text-navy/40"><ShieldCheck size={11} /> Share photos of the problem · phone numbers, emails & UPI IDs are hidden automatically.</p>
                        </div>
                    )}
                </div>
            )}
            {zoom && (
                <div data-testid="chat-photo-zoom" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4" onClick={() => setZoom(null)}>
                    <button className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white"><X size={20} /></button>
                    <img src={img(zoom)} alt="Shared" className="max-h-[85vh] max-w-full rounded-xl object-contain" />
                </div>
            )}
        </div>
    );
}
